// src/pages/ProductDetailPage.js — Premium product detail
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productsApi, unwrap, ApiError, getImageUrl, getImageUrlFallback } from '../services/api';
import { useCart }  from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { Badge } from '../components/ui';
import { Skeleton } from '../components/common/ErrorBoundary';

function DetailSkeleton() {
  return (
    <div className="bg-white rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.07)] overflow-hidden grid md:grid-cols-2">
      <Skeleton className="h-96 rounded-none"/>
      <div className="p-10 space-y-5">
        <Skeleton className="h-3 w-28"/><Skeleton className="h-10 w-3/4"/><Skeleton className="h-12 w-40"/>
        <Skeleton className="h-4 w-52"/><Skeleton className="h-20 w-full"/><Skeleton className="h-14 w-full"/>
      </div>
    </div>
  );
}

export default function ProductDetailPage() {
  const { slug }              = useParams();
  const { addItem, isInCart } = useCart();
  const { toast }             = useToast();

  const [product,  setProduct] = useState(null);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);
  const [qty,      setQty]     = useState(1);
  const [added,    setAdded]   = useState(false);

  const fetchProduct = () => {
    setLoading(true); setError(null);
    productsApi.detail(slug)
      .then(res => {
        const data = unwrap(res);
        setProduct(data || null);
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    setProduct(null);
    setError(null);
    fetchProduct();
  }, [slug]); // eslint-disable-line

  const inCart     = product ? isInCart(product.id) : false;
  const isNotFound = error instanceof ApiError && error.status === 404;
  const imageUrl   = product ? getImageUrl(product) : getImageUrlFallback();
  const fallbackImg = getImageUrlFallback();

  const handleAdd = () => {
    if (!product?.in_stock) return;
    addItem(product, qty);
    setAdded(true);

    // Sparkle burst
    const emojis = ['✨','🎆','⭐','💥','🌟','🎇'];
    for (let i = 0; i < 6; i++) {
      const el = document.createElement('div');
      el.className = 'sparkle-particle';
      el.textContent = emojis[i];
      el.style.left = `${window.innerWidth/2 + (Math.random()-0.5)*60}px`;
      el.style.top  = `${window.innerHeight/2}px`;
      el.style.animationDelay = `${i*0.1}s`;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 1000);
    }

    toast.success(`✨ "${product.name}" — ${qty} unit${qty>1?'s':''} added to cart!`);
    setTimeout(() => setAdded(false), 2200);
  };

  return (
    <div className="min-h-screen py-10" style={{background:'#fffaf0'}}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-gray-400 mb-8 font-medium animate-fade-in">
          <Link to="/" className="hover:text-red-600 transition-colors">Home</Link>
          <span className="text-gray-300">/</span>
          <Link to="/products" className="hover:text-red-600 transition-colors">Products</Link>
          {product && (<><span className="text-gray-300">/</span><span className="text-gray-600">{product.name}</span></>)}
        </nav>

        {loading && <DetailSkeleton/>}

        {!loading && error && (
          isNotFound ? (
            <div className="text-center py-24 animate-fade-in">
              <div className="text-7xl mb-5">🔍</div>
              <h2 className="text-3xl font-black text-gray-800 mb-3" style={{fontFamily:'"Playfair Display",serif'}}>Product Not Found</h2>
              <p className="text-gray-500 text-sm mb-8">This product may have been removed or the link is incorrect.</p>
              <Link to="/products"
                className="bg-gradient-to-br from-red-600 to-red-800 text-white font-black px-10 py-4 rounded-2xl
                  shadow-xl shadow-red-600/30 hover:-translate-y-1 transition-all duration-200 inline-block">
                Browse All Products
              </Link>
            </div>
          ) : (
            <div className="text-center py-16 animate-fade-in">
              <div className="text-6xl mb-4">😴</div>
              <h2 className="text-2xl font-black text-gray-800 mb-2">
                Server is Starting Up...
              </h2>
              <p className="text-gray-500 mb-6 max-w-md mx-auto text-sm">
                Our server takes a moment to wake up on first visit.
                Please click Try Again — it will load in a few seconds!
              </p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => { setError(null); fetchProduct(); }}
                  className="bg-red-600 hover:bg-red-700 text-white font-black
                    px-8 py-3 rounded-2xl transition-colors">
                  🔄 Try Again
                </button>
                <Link to="/products"
                  className="border-2 border-red-200 text-red-600 hover:bg-red-50
                    font-black px-8 py-3 rounded-2xl transition-colors">
                  ← All Products
                </Link>
              </div>
            </div>
          )
        )}

        {!loading && product && (
          <div className="bg-white rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.07)] overflow-hidden grid md:grid-cols-2 animate-scale-in">
            {/* Image panel */}
            <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-red-50
              flex items-center justify-center p-10 min-h-80 overflow-hidden group">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500
                bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none"/>
              <img src={imageUrl} alt={product?.name || 'Product'}
              loading="lazy" decoding="async" width="600" height="400"
                onError={e=>{e.target.src=fallbackImg;}}
                className="max-w-sm w-full object-contain rounded-2xl
                  group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.discount_percent>0 && <Badge color="red">{product.discount_percent}% OFF</Badge>}
                {product.is_featured && <Badge color="gold">⭐ Featured</Badge>}
              </div>
            </div>

            {/* Details panel */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <p className="text-[10px] font-black text-red-500 uppercase tracking-[0.2em] mb-3">
                {product.category_name}
              </p>
              <h1 className="text-3xl font-black text-gray-900 mb-5 leading-tight"
                style={{fontFamily:'"Playfair Display",serif'}}>
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-5">
                <span className="text-5xl font-black"
                  style={{fontFamily:'"Plus Jakarta Sans",sans-serif', color:'#b91c1c', letterSpacing:'-0.02em'}}>
                  ₹{product.price}
                </span>
                {product.original_price && (
                  <span className="text-xl text-gray-400 line-through">₹{product.original_price}</span>
                )}
                {product.discount_percent>0 && (
                  <span className="text-sm font-black text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                    Save ₹{(parseFloat(product.original_price||0)-parseFloat(product.price||0)).toFixed(0)}
                  </span>
                )}
              </div>

              {/* Stock */}
              <div className="flex items-center gap-2.5 mb-6">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  product.stock>10?'bg-green-500':product.stock>0?'bg-amber-500 animate-pulse':'bg-red-500'}`}/>
                <span className={`text-sm font-bold ${
                  product.stock>10?'text-green-700':product.stock>0?'text-amber-700':'text-red-600'}`}>
                  {product.stock>10?`In Stock (${product.stock} units)`:product.stock>0?`Only ${product.stock} left — order soon!`:'Out of Stock'}
                </span>
              </div>

              {/* Unit type */}
              {product.unit_type && (() => {
                const unitLabels = {
                  box:'Box', pkt:'Packet', piece:'Piece',
                  set:'Set', dozen:'Dozen', bundle:'Bundle', roll:'Roll'
                };
                const unitLabel = unitLabels[product.unit_type] || product.unit_type;
                return (
                  <div className="mb-3">
                    <span className="inline-flex items-center gap-1.5
                      bg-amber-50 border border-amber-200 text-amber-800
                      text-xs font-black px-3 py-1.5 rounded-full uppercase tracking-wider">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 3a1 1 0 00-1 1v1h14V4a1 1 0 00-1-1H4zm-1 4v9a1 1 0 001 1h12a1 1 0 001-1V7H3z"/>
                      </svg>
                      {unitLabel}
                    </span>
                  </div>
                );
              })()}

              {product.description && (
                <p className="text-gray-500 text-sm leading-relaxed mb-7 border-l-2 border-yellow-400/40 pl-4">
                  {product.description}
                </p>
              )}

              {/* Qty picker */}
              {product.in_stock && (
                <div className="flex items-center gap-4 mb-7">
                  <span className="text-sm font-bold text-gray-600">Quantity:</span>
                  <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                    <button onClick={()=>setQty(q=>Math.max(1,q-1))}
                      className="px-4 py-2.5 hover:bg-red-50 hover:text-red-600 text-gray-600 font-bold text-lg transition-colors">
                      −
                    </button>
                    <span className="px-6 py-2.5 font-black text-gray-800 min-w-[3.5rem] text-center text-lg"
                      style={{fontFamily:'"Playfair Display",serif'}}>
                      {qty}
                    </span>
                    <button onClick={()=>setQty(q=>Math.min(product.stock,q+1))}
                      className="px-4 py-2.5 hover:bg-red-50 hover:text-red-600 text-gray-600 font-bold text-lg transition-colors">
                      +
                    </button>
                  </div>
                  {product.min_order>1 && (
                    <span className="text-xs text-gray-400 font-medium">Min: {product.min_order}</span>
                  )}
                </div>
              )}

              {/* CTAs */}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handleAdd}
                  disabled={!product.in_stock}
                  className={`flex-1 py-4 rounded-2xl font-black text-base
                    transition-all duration-300 relative overflow-hidden group
                    ${!product.in_stock
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : added
                      ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/30'
                      : 'bg-gradient-to-br from-red-600 to-red-800 text-white shadow-xl shadow-red-600/30 hover:shadow-red-600/50 hover:-translate-y-0.5 active:scale-95'}`}
                >
                  {!product.in_stock
                    ? 'Out of Stock'
                    : added
                    ? <span className="flex items-center justify-center gap-2">✓ Added to Cart!</span>
                    : <span className="flex items-center justify-center gap-2">
                        <span className="group-hover:translate-x-0 translate-x-0">🛒</span> Add to Cart
                      </span>
                  }
                </button>
                {inCart && (
                  <Link to="/cart"
                    className="flex-1 border-2 border-red-600/30 text-red-600 hover:border-red-600
                      hover:bg-red-50 font-black py-4 px-6 rounded-2xl text-base text-center transition-all">
                    View Cart →
                  </Link>
                )}
              </div>

              {product.tags && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {product.tags.split(',').map(t=>t.trim()).filter(Boolean).map(t => (
                    <span key={t} className="bg-amber-100/80 text-amber-700 text-xs font-semibold
                      px-3 py-1 rounded-full border border-amber-200/50">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-center mt-10">
          <Link to="/products"
            className="text-red-600 hover:text-red-800 font-bold text-sm hover:underline
              flex items-center justify-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            </svg>
            Back to all products
          </Link>
        </div>
      </div>
    </div>
  );
}
